/**
 * ChatInputV2 - Mantine-based chat input component
 *
 * Provides a textarea for message input with send button.
 * Supports disabled states and auto-resize.
 * Enter key creates newline, click button to send.
 */

import { forwardRef, useState, useCallback } from 'react';
import { Box, Textarea, ActionIcon, Tooltip } from '@mantine/core';
import { AiOutlineSend } from 'react-icons/ai';

const MAX_MESSAGE_LENGTH = 5000;

interface ChatInputV2Props {
  /** Callback when message is submitted */
  onSubmit: (message: string) => void;
  /** Whether input is disabled (e.g., during sending) */
  disabled?: boolean;
  /** Whether input is disabled for this member (permission issue) */
  disabledForMember?: boolean;
  /** Tooltip message when disabled for member */
  disabledTooltip?: string;
  /** Whether a message is currently being sent */
  isSending?: boolean;
  /** Placeholder text */
  placeholder?: string;
}

const ChatInputV2 = forwardRef<HTMLTextAreaElement, ChatInputV2Props>(
  (
    {
      onSubmit,
      disabled = false,
      disabledForMember = false,
      disabledTooltip = 'Only tagged internal team members can send messages. Please ask your admin to add you.',
      isSending = false,
      placeholder,
    },
    ref
  ) => {
    const [messageText, setMessageText] = useState('');

    // Derive placeholder
    const getDefaultPlaceholder = () => {
      if (isSending) return 'Sending message...';
      if (disabledForMember) return 'Messaging not available';
      return 'Enter your message here...';
    };
    const derivedPlaceholder = placeholder || getDefaultPlaceholder();

    // Handle submit (click only - Enter creates newline)
    const handleSubmit = useCallback(() => {
      const trimmed = messageText.trim();
      if (trimmed.length === 0) return;

      onSubmit(trimmed);
      setMessageText('');
    }, [messageText, onSubmit]);

    const isInputDisabled = disabled || disabledForMember || isSending;
    const isSendDisabled = isInputDisabled || messageText.trim().length === 0;

    const sendButton = (
      <ActionIcon
        variant={messageText.trim().length > 0 ? 'filled' : 'light'}
        color={messageText.trim().length > 0 ? 'green' : 'gray'}
        size="lg"
        onClick={handleSubmit}
        disabled={isSendDisabled}
        loading={isSending}
        aria-label="Send message"
        data-testid="send-button"
      >
        <AiOutlineSend size={20} />
      </ActionIcon>
    );

    const inputElement = (
      <Textarea
        ref={ref}
        value={messageText}
        onChange={(e) =>
          setMessageText(e.currentTarget.value.slice(0, MAX_MESSAGE_LENGTH))
        }
        placeholder={derivedPlaceholder}
        disabled={isInputDisabled}
        maxLength={MAX_MESSAGE_LENGTH}
        autosize
        minRows={1.5}
        maxRows={6}
        rightSection={sendButton}
        rightSectionWidth={44}
        styles={{
          input: {
            opacity: isSending ? 0.6 : 1,
          },
          section: {
            alignItems: 'flex-end',
            paddingBottom: 4,
          },
        }}
        data-testid="chat-input"
      />
    );

    return (
      <Box p="sm" px="md">
        {disabledForMember ? (
          <Tooltip label={disabledTooltip} multiline w={250}>
            {inputElement}
          </Tooltip>
        ) : (
          inputElement
        )}
      </Box>
    );
  }
);

ChatInputV2.displayName = 'ChatInputV2';

export default ChatInputV2;
